
import { computed, defineComponent, nextTick, ref, toRef, watch } from "vue";
import { Form, Field, ErrorMessage } from "vee-validate";

import { FormatMethods, getMaxLengthErrorMessage } from "@/helpers/data-format";
import { LookupAPI } from "@/services/api/Lookup.service";
import * as Yup from "yup";
import { hideModal } from "@/core/helpers/dom";

import { useToast } from "vue-toastification";
import { ICloneLookup } from "@/interfaces/iLookup";
import { useI18n } from "vue-i18n/index";
import Datepicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import moment from "moment-timezone";

export default defineComponent({
  name: "user-clone-modal",
  emit: ["clone-submit"],
  props: {
    lookup: { type: Object, default: null },
  },
  components: {
    Form,
    Field,
    ErrorMessage,
    Datepicker,
  },
  setup(props, { emit }) {
    const { t, te } = useI18n();
    function translate(key: string) {
      if (te(key)) {
        return t(key);
      } else {
        return key.split(".").join(" ");
      }
    }
    const toast = useToast();
    const cloneSuccessMsg = translate("pages.lookup.lookupCloneSuccess");
    const newTargetCloneModalRef = ref<null | HTMLElement>(null);
    const cloneLookupRef = ref<null | HTMLFormElement>(null);
    const loading = ref(false);
    let lookupObj = ref<any>({});
    const cloneData = toRef(props, "lookup");
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let lookupClone: ICloneLookup;
    let cloneTargetData = ref<any>({});
    let cloneLookupData = ref({
      validationSchema: Yup.object({
        lookupName: Yup.string()
          .max(30, getMaxLengthErrorMessage(t("common.name"), 30))
          .required(t("common.name") + " " + t("common.isRequired"))
          .matches(/^LK[a-zA-Z0-9]+/, t("pages.lookup.lookupNameValidation")),
        startDate: Yup.date()
          .nullable()
          .required(t("common.startDate") + " " + t("common.isRequired")),
        endDate: Yup.date()
          .nullable()
          .min(Yup.ref("startDate"), "End date can't be before Start date"),
      }),
    });
    lookupObj.value = computed(() => {
      return props.lookup;
    }).value;

    const discard = () => {
      var appElement = document.getElementById("app");
      if (appElement) {
        appElement.click();
      }
      hideModal(newTargetCloneModalRef.value);
      nextTick(() => {
        cloneLookupRef?.value?.resetForm();
      });
      emit("on-cancel", null);
    };

    const format = computed(() => {
      return FormatMethods;
    });

    const disablePreviousDates = (time) => {
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);
      return time.getTime() < currentDate.getTime();
    };

    const displayDate = computed(() => {
      return process.env.VUE_APP_DATE_PICKER_FORMAT || "MM-dd-yyyy";
    });
    const setLookupCloneData = async () => {
      try {
        loading.value = true;
        const [lookupModelList] = await Promise.all([
          LookupAPI.getInstance().getLookupModelList({
            size: 100000,
            status: "ACTIVE",
            lookupId: lookupObj.value.webId,
          }),
        ]);
        if (lookupModelList.content && lookupModelList.content.length > 0) {
          cloneLookupData.value["cloneDataModel"] = lookupModelList.content;
        }
        cloneTargetData.value["cloneDataModel"] = lookupModelList.content.map(
          (obj: any) => {
            return obj.webId;
          }
        );
      } catch (error) {
        console.log(error);
      } finally {
        loading.value = false;
      }
    };

    const submitCloneData = async () => {
      try {
        loading.value = true;
        lookupClone = {} as ICloneLookup;
        lookupClone.lookupName = cloneTargetData.value.lookupName;

        const dateFormat = "YYYY-MM-DDTHH:mm:ssZ";
        const startMoment = moment(cloneTargetData.value.startDate).startOf(
          "day"
        );
        const formattedStartDate = startMoment.format(dateFormat);
        lookupClone.startDate = formattedStartDate;

        if (cloneTargetData.value.endDate) {
          const endMoment = moment(cloneTargetData.value.endDate).endOf("day");
          const formattedEndDate = endMoment.format(dateFormat);
          lookupClone.endDate = formattedEndDate;
        }
        lookupClone.status = "ACTIVE";
        lookupClone.lookupDataGlobalModel = {
          webIds:
            cloneTargetData?.value?.["cloneDataModel"]?.length > 0
              ? cloneTargetData?.value?.["cloneDataModel"]
              : [],
        };
        await LookupAPI.getInstance().submitCloneLookup(lookupClone);
        toast.success(cloneSuccessMsg);
        discard();
        emit("clone-submit");
      } catch (error) {
        console.log(error);
      } finally {
        loading.value = false;
      }
    };

    const clearFormData = () => {
      cloneLookupRef?.value?.resetForm();
      cloneTargetData.value = {};
    };

    const selectAll = (element, array, key?) => {
      if (element.srcElement.checked) {
        cloneTargetData.value[array] = cloneLookupData.value[array]?.map(
          (obj) => {
            return key ? obj[key] : obj.webId;
          }
        );
      } else {
        cloneTargetData.value[array] = [];
      }
    };

    const isChecked = (array, id) => {
      return (
        cloneTargetData?.value[array]?.filter((objId) => objId == id).length > 0
      );
    };

    const minDate = computed(() => {
      return moment().format("YYYY-MM-DD");
    });
    const minEndDate = computed(() => {
      const today = moment().format("YYYY-MM-DD");
      const startDate = moment(cloneTargetData.value.startDate).format(
        "YYYY-MM-DD"
      );
      if (today >= startDate) {
        return today;
      } else {
        return startDate;
      }
    });

    const updateCloneArray = (element, id, objectId) => {
      if (element.srcElement.checked) {
        if (cloneTargetData?.value[objectId] == undefined) {
          cloneTargetData.value[objectId] = [];
        }
        cloneTargetData?.value[objectId].push(id);
      } else {
        cloneTargetData.value[objectId] = cloneTargetData?.value[
          objectId
        ].filter((objId) => objId != id);
      }
    };
    watch(
      cloneData,
      (newValue) => {
        lookupObj.value = { ...newValue };
        clearFormData();
        setLookupCloneData();
      },
      { immediate: true }
    );
    return {
      translate,
      setLookupCloneData,
      submitCloneData,
      cloneTargetData,
      updateCloneArray,
      newTargetCloneModalRef,
      format,
      cloneLookupRef,
      discard,
      lookupObj,
      isChecked,
      selectAll,
      loading,
      displayDate,
      disablePreviousDates,
      minDate,
      minEndDate,
      cloneLookupData,
    };
  },
});
